import type { ReactNode } from 'react';

import { ExitBadge } from '@entities/investment/@x/fund';
import { cn } from '@shared/lib/cn';
import { Badge } from '@shared/ui/badge';
import { Card } from '@shared/ui/card';
import { Icon, type StatIconName } from '@shared/ui/icon';
import { StatCard } from '@shared/ui/stat-card';

import { formatFundPercent, formatRatio2 } from '../lib/fund-format';
import {
  FUND_METRIC_ICON_CIRCLE_CLASS,
  FUND_METRIC_PAIR_GAP_CLASS,
} from './fund-metric-card.constant';
import { FUND_METRIC_CARD_TEXT } from './fund-metric-card.text';
import { FundMetricHelp } from './fund-metric-help.ui';

export type FundMetricCardProps = { title: string; help?: readonly string[] } & (
  | {
      kind: 'single';
      icon: boolean;
      iconName?: StatIconName;
      /** 값 글자. 자료를 받는 중이면 그 자리에 스켈레톤을 끼운다(MainSummary status=init) */
      value: ReactNode;
    }
  | {
      kind: 'exit-count';
      icon: boolean;
      iconName?: StatIconName;
      complete: number;
      partial: number;
    }
  | { kind: 'irr'; icon: false; gross: number | null; net: number | null }
  | { kind: 'rvpi-dpi'; icon: false; rvpi: number | null; dpi: number | null }
);

interface Pair {
  badge: ReactNode;
  value: string;
}

const countText = (count: number) => `${count}${FUND_METRIC_CARD_TEXT.countUnit}`;

/** 펀드 · 전체 요약 지표 카드. 값 하나(StatCard), 또는 배지 + 값 두 쌍(회수 건수 · IRR · RVPI/DPI) */
export function FundMetricCard(props: FundMetricCardProps) {
  const { title, help } = props;
  const helpNode = help && help.length > 0 ? <FundMetricHelp title={title} help={help} /> : null;
  // 통계 아이콘 색 = brand primary(#00ADA0, 기존 서비스 ico_main_* 의 선 색과 같다 — DD-22 1)
  const iconNode =
    props.kind === 'single' || props.kind === 'exit-count' ? (
      props.icon ? (
        <Icon name={props.iconName} size="xl" className="text-brand-primary" />
      ) : null
    ) : null;

  if (props.kind === 'single') {
    return (
      <StatCard
        title={title}
        value={props.value || '-'}
        layout="vertical"
        valueSize="md"
        showHelp={helpNode !== null}
        help={helpNode ?? undefined}
        icon={iconNode ?? undefined}
      />
    );
  }

  let pairs: Pair[];
  let gap: (typeof FUND_METRIC_PAIR_GAP_CLASS)[keyof typeof FUND_METRIC_PAIR_GAP_CLASS] =
    FUND_METRIC_PAIR_GAP_CLASS.wide;
  if (props.kind === 'exit-count') {
    pairs = [
      { badge: <ExitBadge type="complete-exit" />, value: countText(props.complete) },
      { badge: <ExitBadge type="partial-exit" />, value: countText(props.partial) },
    ];
  } else if (props.kind === 'irr') {
    pairs = [
      {
        badge: (
          <Badge tone="secondary" appearance="solid">
            {FUND_METRIC_CARD_TEXT.grossIrr}
          </Badge>
        ),
        value: formatFundPercent(props.gross),
      },
      {
        badge: (
          <Badge tone="secondary" appearance="solid">
            {FUND_METRIC_CARD_TEXT.netIrr}
          </Badge>
        ),
        value: formatFundPercent(props.net),
      },
    ];
  } else {
    gap = FUND_METRIC_PAIR_GAP_CLASS.narrow;
    pairs = [
      {
        badge: (
          <Badge tone="neutral" appearance="subtle">
            {FUND_METRIC_CARD_TEXT.rvpi}
          </Badge>
        ),
        value: formatRatio2(props.rvpi),
      },
      {
        badge: (
          <Badge tone="neutral" appearance="subtle">
            {FUND_METRIC_CARD_TEXT.dpi}
          </Badge>
        ),
        value: formatRatio2(props.dpi),
      },
    ];
  }

  return (
    <Card data-kind={props.kind} className="w-full flex-row items-center justify-between py-4">
      <dl className="flex min-w-0 flex-1 flex-col items-start gap-2">
        <dt className="flex items-center gap-1">
          <span className="text-fg-muted typo-caption">{title}</span>
          {helpNode}
        </dt>
        <dd className={cn('flex flex-wrap items-center', gap.row)}>
          {pairs.map((pair, index) => (
            <span key={index} className={cn('flex shrink-0 items-center', gap.pair)}>
              <span className="inline-flex shrink-0">{pair.badge}</span>
              <span className="text-fg-default typo-title">{pair.value}</span>
            </span>
          ))}
        </dd>
      </dl>
      {iconNode ? (
        <span data-slot="icon" className={FUND_METRIC_ICON_CIRCLE_CLASS} aria-hidden>
          {iconNode}
        </span>
      ) : null}
    </Card>
  );
}
